import React from 'react';
import { Tooltip, AIAssistButton } from './Shared';

function NumberField({ label, tooltip, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Tooltip text={tooltip}>
        <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      </Tooltip>
      <input
        type="number"
        value={value ?? ''}
        onChange={onChange}
        className="w-full h-12 px-4 rounded-xl border border-slate-200"
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}

export function SectionC({ data, updateSection }) {
  const sc = data.section_c;
  const num = (field, parser = parseFloat) => (e) => {
    const v = parser(e.target.value);
    updateSection('section_c', field, Number.isNaN(v) ? null : v);
  };
  const pct = (field) => (e) => {
    const raw = parseFloat(e.target.value);
    updateSection('section_c', field, Number.isNaN(raw) ? null : Math.min(100, Math.max(0, raw)));
  };
  const estimatedEmissions = sc.electricity_kwh > 0 ? Math.round(sc.electricity_kwh * 0.0007) : 0;

  return (
    <div className="space-y-8" data-testid="section-c-content">
      {/* Data Privacy */}
      <div className="section-card">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-primary">1. Data Privacy & Security Compliance</h3>
          <AIAssistButton topic="Data Privacy & Security Compliance KPIs" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <NumberField
            label="1.1 Number of Reported Data Breaches"
            tooltip="Include confirmed data breaches reported internally or to regulators within the reporting year. Data source: IT Security incident logs, NPC (National Privacy Commission) reports. If need more information, click the AI Assist button."
            value={sc.data_breaches ?? ''}
            onChange={num('data_breaches', parseInt)}
            placeholder="e.g., 0"
            testId="data-breaches-input"
          />
          <div>
            <Tooltip text="Refers to documented policies aligned with GDPR, PDPA, ISO 27001, or equivalent. Data source: Compliance/Legal department, policy documentation. If need more information, click the AI Assist button.">
              <label className="block text-sm font-medium text-slate-700 mb-2">1.2 Formal Data Privacy Policy in Place?</label>
            </Tooltip>
            <div className="flex gap-3">
              {['Yes', 'No'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => updateSection('section_c', 'privacy_policy', opt === 'Yes')}
                  className={`flex-1 h-12 rounded-xl border font-medium transition-all ${
                    sc.privacy_policy === (opt === 'Yes')
                      ? 'border-accent bg-accent/5 text-accent'
                      : 'border-slate-200 text-slate-600'
                  }`}
                  data-testid={`privacy-policy-${opt.toLowerCase()}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Talent */}
      <div className="section-card">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-primary">2. Talent Acquisition & Retention</h3>
          <AIAssistButton topic="Talent Acquisition & Retention KPIs" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <NumberField
            label="2.1 Annual Employee Attrition Rate (%)"
            tooltip="Data source: HR system, monthly separation reports. Formula: Attrition Rate = (Total Separations ÷ Average Headcount) × 100. If need more information, click the AI Assist button."
            value={sc.attrition_rate ?? ''}
            onChange={pct('attrition_rate')}
            placeholder="e.g., 18"
            testId="attrition-rate-input"
          />
          <NumberField
            label="2.2 Average Training Hours per Employee"
            tooltip="Include structured learning programs, certifications, and digital upskilling. Data source: L&D/Training department records. Formula: Avg Hours = Total Training Hours ÷ Total Employees. If need more information, click the AI Assist button."
            value={sc.training_hours ?? ''}
            onChange={num('training_hours')}
            placeholder="e.g., 42"
            testId="training-hours-input"
          />
        </div>
      </div>

      {/* Energy & Emissions */}
      <div className="section-card">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-primary">3. Energy and Emissions</h3>
          <AIAssistButton topic="Energy Consumption and Carbon Emissions KPIs" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <NumberField
            label="3.1 Annual Electricity Consumption (kWh)"
            tooltip="Data source: Utility bills (Meralco, etc.), building management reports. Sum all monthly electricity bills for the reporting year. If need more information, click the AI Assist button."
            value={sc.electricity_kwh ?? ''}
            onChange={num('electricity_kwh', parseInt)}
            placeholder="e.g., 2400000"
            testId="electricity-input"
          />
          <NumberField
            label="3.2 Renewable Energy (%)"
            tooltip="Include green power contracts or renewable share from building provider. Data source: Utility provider certificates, building green energy reports. If need more information, click the AI Assist button."
            value={sc.renewable_energy_pct ?? ''}
            onChange={pct('renewable_energy_pct')}
            placeholder="e.g., 25"
            testId="renewable-energy-input"
          />
          <div>
            <Tooltip text="Data source: Carbon accounting system, or calculate from energy data. Formula: Scope 2 Emissions = Total kWh × 0.0007 tCO₂e/kWh (PH grid factor). Scope 1 includes direct fuel combustion (generators, vehicles). If need more information, click the AI Assist button.">
              <label className="block text-sm font-medium text-slate-700 mb-2">3.3 Scope 1 & 2 Emissions (tCO₂e)</label>
            </Tooltip>
            <div className="space-y-2">
              <input
                type="number"
                value={sc.carbon_emissions_na ? '' : (sc.carbon_emissions ?? '')}
                onChange={num('carbon_emissions', parseInt)}
                disabled={sc.carbon_emissions_na}
                className={`w-full h-12 px-4 rounded-xl border border-slate-200 ${sc.carbon_emissions_na ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                placeholder="e.g., 1320"
                data-testid="carbon-emissions-input"
              />
              {!sc.carbon_emissions_na && estimatedEmissions > 0 && (sc.carbon_emissions === null || sc.carbon_emissions === undefined) && (
                <button
                  type="button"
                  onClick={() => updateSection('section_c', 'carbon_emissions', estimatedEmissions)}
                  className="w-full text-left text-xs text-accent-dark bg-accent/5 border border-accent/30 rounded-lg px-3 py-2 hover:bg-accent/10 transition-all"
                  data-testid="use-emissions-estimate-btn"
                >
                  Estimated from your 3.1 electricity entry: ~{estimatedEmissions.toLocaleString()} tCO₂e — click to use this estimate
                </button>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sc.carbon_emissions_na || false}
                  onChange={(e) => {
                    updateSection('section_c', 'carbon_emissions_na', e.target.checked);
                    if (e.target.checked) {
                      updateSection('section_c', 'carbon_emissions', null);
                    }
                  }}
                  className="w-4 h-4 rounded border-slate-300 text-accent focus:ring-accent"
                  data-testid="carbon-emissions-na"
                />
                Emission data not available
              </label>
            </div>
          </div>
          <NumberField
            label="3.4 IT Equipment Recycled Responsibly (%)"
            tooltip="Data source: IT Asset Management, disposal/recycling vendor records. Formula: Recycled % = (Units Recycled Responsibly ÷ Total Disposed Units) × 100. If need more information, click the AI Assist button."
            value={sc.ewaste_recycled_pct ?? ''}
            onChange={pct('ewaste_recycled_pct')}
            placeholder="e.g., 85"
            testId="ewaste-recycled-input"
          />
        </div>
      </div>

      {/* Community & Workforce */}
      <div className="section-card">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-semibold text-primary">4. Community & Workforce</h3>
          <AIAssistButton topic="Community Development and Workforce KPIs" />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <NumberField
            label="4.1 Workforce Hired Locally (%)"
            tooltip="Data source: HR recruitment records. Formula: Local Hiring % = (Filipino Employees ÷ Total Employees) × 100. If need more information, click the AI Assist button."
            value={sc.local_hiring_pct ?? ''}
            onChange={pct('local_hiring_pct')}
            placeholder="e.g., 92"
            testId="local-hiring-input"
          />
          <NumberField
            label="4.2 Community Investment Spend (PHP)"
            tooltip="Data source: CSR department, Finance records for donations/programs. Sum all community-related expenditures: scholarships, donations, livelihood programs. If need more information, click the AI Assist button."
            value={sc.community_investment ?? ''}
            onChange={num('community_investment', parseInt)}
            placeholder="e.g., 5000000"
            testId="community-investment-input"
          />
          <NumberField
            label="4.3 Roles Augmented/Automated by AI (%)"
            tooltip="Data source: Operations/Workforce planning, automation project reports. Estimate % of FTEs whose roles have been significantly changed by AI/automation. If need more information, click the AI Assist button."
            value={sc.ai_automation_pct ?? ''}
            onChange={pct('ai_automation_pct')}
            placeholder="e.g., 20"
            testId="ai-automation-input"
          />
          <NumberField
            label="4.4 Employees Earning ≥ Living Wage (%)"
            tooltip="Data source: Payroll data compared to DOLE regional living wage standards. Formula: Living Wage % = (Employees earning ≥ Living Wage ÷ Total Employees) × 100. If need more information, click the AI Assist button."
            value={sc.living_wage_pct ?? ''}
            onChange={pct('living_wage_pct')}
            placeholder="e.g., 100"
            testId="living-wage-input"
          />
        </div>
      </div>
    </div>
  );
}
